<script setup>
import EnvelopeIcon from '@iconify-vue/ph/x';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { toastError, toastSuccess } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { object, string } from 'yup';

const { tenant, emailingAutomation } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailingAutomation: {
        type: Object,
        required: true,
    },
});

const isOpen = ref(false);

const isSubmitting = ref(false);

const templateOptions = computed(() => [
    { label: 'Email initial', value: null },
    ...(emailingAutomation.steps ?? []).map((step) => ({
        label: `Relance ${step.position - 1}`,
        value: step.id,
    })),
]);

const selectedTemplate = ref(templateOptions.value[0]);

const form = reactive({
    email: '',
});

const validationSchema = object({
    email: string()
        .email('Veuillez entrer une adresse email valide')
        .required('Le champ email est requis'),
});

function sendTestEmail() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.sendEmailingAutomationTestEmail',
                {
                    tenant: tenant.id,
                    emailingAutomation: emailingAutomation.id,
                },
            ),
            {
                email: form.email,
                stepId: selectedTemplate.value.value,
            },
        )
        .then(() => {
            toastSuccess('Email de test envoyé avec succès');
            isOpen.value = false;
            form.email = '';
        })
        .catch(() => {
            toastError(
                "Une erreur est survenue lors de l'envoi de l'email de test.",
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <DialogModal v-model="isOpen" size="small">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Envoyer un email de test </template>

        <template #full-content>
            <FormRoot
                :submit="sendTestEmail"
                :validation-schema="validationSchema"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent v-if="templateOptions.length > 1">
                        <ListboxField
                            v-model="selectedTemplate"
                            field-id="stepId"
                            field-name="stepId"
                            label="Template à tester"
                            :options="templateOptions"
                            by="value"
                        >
                            <template #buttonContent>
                                {{ selectedTemplate.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>
                    </FormContent>

                    <FormContent>
                        <div class="mb-4 flex flex-wrap gap-2">
                            <ActionButton
                                :callback="
                                    () => (form.email = 'anthony@apikom.fr')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                anthony@apikom.fr
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () => (form.email = 'valere@apikom.fr')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                valere@apikom.fr
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () => (form.email = 'sophie@apikom.fr')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                sophie@apikom.fr
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () => (form.email = 'benoit@apikom.fr')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                benoit@apikom.fr
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () => (form.email = 'nicolas@apikom.fr')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                nicolas@apikom.fr
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () => (form.email = 'theo@apikom.fr')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                theo@apikom.fr
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () =>
                                        (form.email =
                                            'anthony.courquin@gmail.com')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                anthony.courquin@gmail.com
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () =>
                                        (form.email = 'valere.fagot@gmail.com')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                valere.fagot@gmail.com
                            </ActionButton>

                            <ActionButton
                                :callback="
                                    () => (form.email = 'smassemin@gmail.com')
                                "
                                size="small"
                                :left-icon="EnvelopeIcon"
                            >
                                smassemin@gmail.com
                            </ActionButton>
                        </div>

                        <InputField
                            v-model="form.email"
                            label="Email"
                            field-name="email"
                            field-id="email"
                            type="email"
                            required
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :isSubmitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                    submit-text="Envoyer"
                    submitting-text="Envoi en cours..."
                />
            </FormRoot>
        </template>
    </DialogModal>
</template>
